// Shrinking a photo before it is uploaded.
//
// A phone camera writes 3-12MB per shot, and a märkus can carry several. The
// server keeps none of that: sharp resizes every upload to
// UPLOAD_MAX_PICTURE_WIDTH (1000px) and throws the rest away. So the whole
// original was being pushed across a technician's mobile connection to store a
// couple of hundred kilobytes — and a note with a few photos made a request
// large enough that it did not arrive at all, which is why the text saved and
// the pictures did not.
//
// Same file in akt/src/plugins — the two apps build separately and share no
// module path, exactly as sanitize.js is duplicated into customer/.

// What the server keeps. Sending more is waste; sending less loses detail the
// server would have kept.
const MAX_WIDTH = 1000

// High enough that a boiler's data plate stays readable, low enough that the
// file is a fraction of the original.
const QUALITY = 0.85

// Decode with the EXIF rotation applied, so the pixels come out the way the
// photo is meant to be seen. Without this a portrait shot from a phone lands
// sideways: the orientation lives in metadata the canvas does not carry over,
// and sharp strips metadata on the way out.
async function decode (file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch (err) {
      // Safari before 15 rejects the options argument rather than ignoring it.
      return await createImageBitmap(file)
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')) }
    img.src = url
  })
}

// The name the shrunk file travels under. It is a JPEG now whatever it was.
const jpegName = name => String(name || 'foto').replace(/\.[^.]+$/, '') + '.jpg'

// One file, shrunk to MAX_WIDTH, or the original when that cannot be done.
// Never throws: a photo we fail to read is still a photo the technician chose,
// and the server can handle it — refusing to upload it would be worse than
// uploading it whole.
async function shrinkOne (file) {
  if (!file || !/^image\//.test(file.type || '')) {
    return { blob: file, name: file && file.name }
  }
  try {
    const source = await decode(file)
    const width = source.width || source.naturalWidth
    const height = source.height || source.naturalHeight
    if (!width || !height || width <= MAX_WIDTH) {
      // Already no bigger than what the server would keep — re-encoding it
      // would only cost a generation of JPEG quality.
      return { blob: file, name: file.name }
    }
    const canvas = document.createElement('canvas')
    canvas.width = MAX_WIDTH
    canvas.height = Math.max(1, Math.round(height * (MAX_WIDTH / width)))
    canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height)
    if (source.close) source.close()
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', QUALITY))
    if (!blob) {
      return { blob: file, name: file.name }
    }
    return { blob, name: jpegName(file.name) }
  } catch (err) {
    return { blob: file, name: file.name }
  }
}

// Every chosen photo, shrunk. Returns { blob, name } pairs ready for FormData.
// One after another: decoded all at once, a dozen photos from the gallery are
// a dozen full-size bitmaps in memory together, and a mid-range phone closes
// the tab over that.
export async function shrinkPhotos (files) {
  const shrunk = []
  for (const file of Array.from(files || [])) {
    shrunk.push(await shrinkOne(file))
  }
  return shrunk
}

export default { shrinkPhotos }
